import { useEffect, useState } from "react";
import { hasRole, useApi, useApp } from "../context/AppContext";
import { WebhooksSection } from "./DevicesPage";

// Integrations (docs/tasks/35, phase 7): who the backend tells about door and
// cart events (webhooks), and who may call it (API keys for partners and the
// web-app front door). A key's plaintext is shown once, when it is made.

const SCOPE_HINT = "devices.door devices.read sessions.read";

function ApiKeys() {
  const api = useApi();
  const { user } = useApp();
  const [keys, setKeys] = useState([]);
  const [form, setForm] = useState({ name: "", scopes: SCOPE_HINT });
  const [made, setMade] = useState(null);
  const [err, setErr] = useState(null);
  const isAdmin = hasRole(user, "tenant_admin");
  const load = () => api.listApiKeys().then(setKeys).catch((e) => setErr(String(e.message || e)));
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [api]);
  const create = async (e) => {
    e.preventDefault(); setErr(null);
    try {
      const k = await api.createApiKey({ name: form.name, scopes: form.scopes.split(/[\s,]+/).filter(Boolean) });
      setMade(k); setForm({ name: "", scopes: SCOPE_HINT }); await load();
    } catch (e2) { setErr(String(e2.message || e2)); }
  };
  const revoke = async (k) => {
    if (!window.confirm(`Revoke "${k.name}"? Every caller using it stops working at once.`)) return;
    try { await api.revokeApiKey(k.id); await load(); } catch (e) { setErr(String(e.message || e)); }
  };
  return (
    <div className="card">
      <h2 className="mb-1 text-sm font-semibold">API keys</h2>
      <p className="mb-3 text-xs text-slate-500">
        A key carries the role <code>api_developer</code> and the scopes given here; door, shelf and weight actions need their scope or an operator role
        (handbook ch. 11). The front-door key with operator roles is minted by <code>tools/mint_front_door_key.py</code>, not here.
      </p>
      {made && (
        <div className="mb-3 rounded border border-emerald-300 bg-emerald-50 p-2 text-xs">
          Key <strong>{made.name}</strong>: <code className="break-all">{made.api_key}</code>
          <div className="text-emerald-800">Copy it now: it is not shown again.</div>
          <button className="mt-1 text-sky-700 hover:underline" onClick={() => setMade(null)}>I have copied it</button>
        </div>
      )}
      {isAdmin && (
        <form onSubmit={create} className="mb-3 grid gap-2 md:grid-cols-[1fr_2fr_auto]">
          <input className="input" required placeholder="name, e.g. merchant X" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input className="input font-mono text-xs" value={form.scopes} onChange={(e) => setForm({ ...form, scopes: e.target.value })} />
          <button className="btn btn-primary" type="submit">Create key</button>
        </form>
      )}
      {err && <p className="mb-2 text-xs text-rose-700">{err}</p>}
      <table className="table">
        <thead><tr><th>Name</th><th>Scopes</th><th>Rate</th><th>Created</th><th>State</th><th /></tr></thead>
        <tbody>
          {keys.map((k) => (
            <tr key={k.id}>
              <td className="text-sm">{k.name}</td>
              <td className="font-mono text-xs">{(k.scopes || []).join(" ") || "—"}</td>
              <td className="text-xs">{k.rate_limit_per_minute}/min</td>
              <td className="text-xs">{k.created_at ? new Date(typeof k.created_at === "number" ? k.created_at * 1000 : k.created_at).toLocaleDateString() : "—"}</td>
              <td className={`text-xs ${k.revoked ? "text-slate-400" : "text-emerald-700"}`}>{k.revoked ? "revoked" : "active"}</td>
              <td>{isAdmin && !k.revoked && <button className="text-xs text-rose-700 hover:underline" onClick={() => revoke(k)}>revoke</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function IntegrationsPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Integrations</h1>
        <p className="text-sm text-slate-500">Webhooks to the sales platform and partners, and the API keys they call with.</p>
      </div>
      <WebhooksSection />
      <ApiKeys />
    </div>
  );
}
